import InvoicePaperView from '../../components/invoice/InvoicePaperView';
import { useEffect, useState } from 'react';
import Button from '../../components/button/Button';
import { useTranslation } from '../../context/translationContext';
import { DocObject, ObjectType, Props, Values } from './SmartAttachment.types';
import { DocumentArrowDownIcon, PlusIcon } from '@heroicons/react/24/outline';
import { useParams } from 'react-router-dom';
import { Form, Formik } from 'formik';
import * as Yup from 'yup';
import FormField from '../../components/field/FormField';
import ButtonMenu from '../../components/buttonMenu/ButtonMenu';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { SmartAttachmentSpreadsheet } from './SmartAttachmentSpreadsheet';
import { Menu } from '@headlessui/react';
import { useLoading } from '../../context/loadingContext';
import { apiUrl } from '../../api/apiUrl';
import { authHeaders } from '../../api/axios';
import { downloadFile } from '../../utils/functions/file.functions';
import http from '../../api/axios';
import { SmartAttachmentEditor } from './SmartAttachmentEditor';

/**
 * Page component represents list of Invoices for current company
 */
export const SmartAttachmentForm = ({
  onSubmit,
  onBack,
  initialValues,
  document,
  isPrint,
}: Props) => {
  const { t } = useTranslation();
  const { documentId, id } = useParams();
  const [objectArr, setObjectArr] = useState<DocObject[]>([]);
  const [selectedIndex, setSelectedIndex] = useState<number>();
  const [objectAdded, setObjectAdded] = useState<boolean>(false);
  const [headerValue, setHeaderValue] = useState<string>('');
  const [documentNumberPrint, setDocumentNumberPrint] = useState<string>();
  const { startLoading, stopLoading } = useLoading();

  const headerObjectIndex = -1;

  // initial setting
  useEffect(() => {
    if (!initialValues) {
      // header in create page
      const str =
        t('page.smart-attachment.header') +
        ': ' +
        (document?.documentNumber ?? documentNumberPrint);
      return setHeaderValue(str);
    }
    // parse body data
    const _document = JSON.parse(initialValues.body);
    // prepare objects
    const objects = _document?.objectArr.map((obj: DocObject) => {
      if (obj.type === ObjectType.TEXTAREA) return { ...obj, textData: obj.textData };
      return obj;
    });
    setHeaderValue(_document?.header);
    setObjectArr(objects);
  }, []);

  const _initialValues: Values = {
    header: '',
    body: '',
    accountingDocumentId: documentId,
    id: initialValues?.id ?? undefined,
  };

  /**
   * Handle text changes in editor
   */
  const onEditorStateChange = (editorState: string, index: number) => {
    const newObjects = objectArr.slice();
    newObjects[index].textData = editorState;
    setObjectArr(newObjects);
  };

  /**
   * Handle text changes for header in editor
   */
  const onHeaderEditorStateChange = (editorState: string) => {
    setHeaderValue(editorState);
  };

  /**
   * Handle changes in the spreadsheet
   */
  const onSpreadsheetChange = (newData: any, index: number) => {
    const newObjects = objectArr.slice();
    newObjects[index].spreadsheetData = newData;

    setObjectArr(newObjects);
  };

  /**
   * Handle changes of spreadsheet styles
   */
  const onSpreadsheetStyleChange = (className: string, index: number) => {
    const newObjects = objectArr.slice();
    newObjects[index].className = className;
    setObjectArr(newObjects);
  };

  /**
   * Prepares & submits data
   */
  const handleOnSubmit = (values: Values) => {
    if (!values.header || !onSubmit) return;
    const _data = initialValues ? initialValues : _initialValues;

    let _objectArr: DocObject[] = [];
    objectArr.map((obj: DocObject) => {
      const html = obj.type === ObjectType.TEXTAREA ? obj.textData : undefined;
      const _obj = {
        id: obj.id,
        type: obj.type,
        textData: html,
        spreadsheetData: obj.spreadsheetData,
        className: obj.className,
      };
      _objectArr = [..._objectArr, _obj];
    });
    const body = {
      header: headerValue,
      objectArr: _objectArr,
    };
    onSubmit({ ..._data, body: JSON.stringify(body), header: values.header });
  };

  /**
   * export pdf by id
   */
  const handleExportPdf = async () => {
    startLoading();
    await http
      .apiGet({
        url: `${apiUrl.SMART_ATTACHMENT_GET_PDF}?id=${id}&documentNumber=${document?.documentNumber}`,
      })
      .then(async (res) => {
        if (!res) return;
        await http
          .apiGetFiles({
            url: `${apiUrl.FILE_DOWNLOAD}?id=${res.data}`,
            config: {
              headers: {
                ...authHeaders(),
                'Content-Type': 'application/json',
              },
              method: 'GET',
              responseType: 'blob',
            },
          })
          .then(async (response) => {
            // get filename
            const filename = await response?.headers['content-disposition']
              ?.split('filename=')[1]
              .split(';')[0];
            const fileUrl: string = URL.createObjectURL(response?.data);
            downloadFile(fileUrl, filename ? filename : 'smart-attachment');
            stopLoading();
          });
      });
    stopLoading();
  };

  useEffect(() => {
    if (!isPrint) return;
    const params = new URLSearchParams(window.location.search);
    const documentNumber = params.get('documentNumber');
    if (documentNumber) setDocumentNumberPrint(documentNumber);
  }, []);

  /**
   * Prepares initial data for new spreadsheet object
   * @param rowNum
   * @param colNum
   */
  const setInitialValuesForSpreadsheet = (rowNum: number, colNum: number) => {
    const defaultSpreadSheet = [];
    // rows
    for (let i = 0; i < rowNum; i++) {
      const row = [];
      // columns
      for (let j = 0; j < colNum; j++) {
        row.push({ value: '', className: '' });
      }
      defaultSpreadSheet.push(row);
    }
    return defaultSpreadSheet;
  };

  /**
   * Handle adding of new object
   * @param type
   */
  const handleAddObject = (type: ObjectType) => {
    // 3 rows, 7 cols by default
    const defaultSpreadSheet = setInitialValuesForSpreadsheet(3, 7);
    const _objectArr = [
      ...objectArr,
      {
        id: objectArr.length,
        type: type,
        spreadsheetData: type === ObjectType.SPREADSHEET ? defaultSpreadSheet : undefined,
        textData: type === ObjectType.TEXTAREA ? '' : undefined,
        className: '',
      } as DocObject,
    ];
    setObjectArr(_objectArr);
    setObjectAdded(true);
  };

  useEffect(() => {
    if (!objectAdded) return;
    setObjectAdded(false);
    setSelectedIndex(objectArr.length - 1);
  }, [objectAdded]);

  /**
   * Handle removing objects
   */
  const handleRemoveObject = (index: number) => {
    const _objectArr = objectArr.filter((x: DocObject) => x.id !== index);
    setSelectedIndex(undefined);
    setObjectArr(_objectArr);
  };

  /**
   *  Form validation
   */
  const validationSchema = Yup.object().shape({
    header: Yup.string().required('Filename is required.'),
  });

  if (isPrint)
    return (
      <InvoicePaperView isPrint={isPrint} className="!mt-3">
        <div className="text-center">
          <div className="h-max-40 text-sm font-normal">
            <div className="mb-5 p-3 relative">
              {headerValue && (
                <SmartAttachmentEditor
                  data={headerValue}
                  onEditorStateChange={onHeaderEditorStateChange}
                  isFocused={selectedIndex === headerObjectIndex}
                />
              )}
            </div>
            {objectArr?.map((obj: DocObject, index: number) => (
              <div key={index} onFocusCapture={() => setSelectedIndex(index)}>
                {/* remove btn */}
                <div className="relative h-10">
                  {selectedIndex === index && (
                    <div className="border absolute bg-red-500 right-[-30px] mt-2.5 justify-center item-center rounded-full hover:bg-red-800 hover:text-white px-1 py-1 focus:outline-none z-30">
                      <XMarkIcon
                        className="cursor-pointer right-1 w-3 h-3 text-white"
                        onClick={() => handleRemoveObject(obj.id)}
                      />
                    </div>
                  )}
                </div>
                {obj?.type === ObjectType.TEXTAREA ? (
                  <SmartAttachmentEditor
                    data={obj.textData}
                    onEditorStateChange={(e: any) => onEditorStateChange(e, index)}
                    index={index}
                    isFocused={index === selectedIndex}
                  />
                ) : (
                  <SmartAttachmentSpreadsheet
                    data={obj?.spreadsheetData}
                    baseClassName={obj?.className}
                    onChange={(e) => onSpreadsheetChange(e, index)}
                    onSpreadsheetChangeStyle={(e) => onSpreadsheetStyleChange(e, index)}
                    isFocused={index === selectedIndex}
                    hideIndicators={true}
                  />
                )}
              </div>
            ))}
          </div>
        </div>
        <>
          {!isPrint && (
            <ButtonMenu
              icon={
                <Menu.Button
                  type="button"
                  className="flex bg-blue-500 justify-center item-center rounded-full bg-blue-500 hover:bg-blue-600 border border-blue-600 hover:border-transparent text-blue-600 hover:text-white px-1 py-1 focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-opacity-75"
                >
                  <PlusIcon className="w-4 h-4 text-white" />
                </Menu.Button>
              }
              options={[
                {
                  title: t('page.smart-attachment.add-opt-textarea'),
                  onClick: () => handleAddObject(ObjectType.TEXTAREA),
                },
                {
                  title: t('page.smart-attachment.add-opt-spreadsheet'),
                  onClick: () => handleAddObject(ObjectType.SPREADSHEET),
                },
              ]}
            />
          )}
        </>
      </InvoicePaperView>
    );
  return (
    <Formik
      enableReinitialize
      initialValues={initialValues || _initialValues}
      onSubmit={(values) => handleOnSubmit(values)}
      validationSchema={validationSchema}
    >
      {({ errors, touched }) => (
        <Form>
          {!isPrint && (
            <div className="absolute text-right right-5 top-20 inline-flex items-center justify-center ">
              <Button title={t('general.back')} color="gray" className="mr-3" onClick={onBack} />
              <Button
                title={t('general.save')}
                className="mr-3"
                color="green"
                type="submit"
                showLoading
              />
              {id && (
                <Button
                  onClick={handleExportPdf}
                  title={t('page.invoice-menu.btn-export-invoice')}
                  type={'button'}
                  icon={
                    <DocumentArrowDownIcon
                      title={t('page.invoice-menu.btn-export-invoice')}
                      className="w-5 h-5 mr-1 ml-0 cursor-pointer"
                    />
                  }
                />
              )}
            </div>
          )}
          <div className="flex w-full attachment-document">
            <div className="w-3/4">
              {!isPrint && (
                <div className="w-1/4 mb-0" style={{ marginLeft: '1cm' }}>
                  <div className="flex flex-row mb-0">
                    <div className="my-4 mb-0 w-full">
                      <FormField
                        id="header"
                        name="header"
                        label={t('page.invoice.input-file-name')}
                        errors={errors}
                        touched={touched}
                      />
                    </div>
                  </div>
                </div>
              )}
              <InvoicePaperView isPrint={isPrint} className="!mt-3">
                <div className="text-center">
                  <div className="text-sm font-normal">
                    <div
                      className="mb-1 relative"
                      onFocusCapture={() => setSelectedIndex(headerObjectIndex)}
                    >
                      {headerValue && (
                        <SmartAttachmentEditor
                          data={headerValue}
                          onEditorStateChange={onHeaderEditorStateChange}
                          isFocused={selectedIndex === headerObjectIndex}
                        />
                      )}
                    </div>
                    {objectArr?.map((obj: DocObject, index: number) => (
                      <div key={index} onFocusCapture={() => setSelectedIndex(index)}>
                        {/* remove btn */}
                        <div className="relative h-10">
                          {selectedIndex === index && (
                            <div className="border absolute bg-red-500 right-[-30px] top-0 justify-center item-center rounded-full hover:bg-red-800 hover:text-white px-1 py-1 focus:outline-none z-30">
                              <XMarkIcon
                                className="cursor-pointer right-1 w-3 h-3 text-white"
                                onClick={() => handleRemoveObject(obj.id)}
                              />
                            </div>
                          )}
                        </div>
                        {obj?.type === ObjectType.TEXTAREA ? (
                          <SmartAttachmentEditor
                            data={obj.textData}
                            onEditorStateChange={(e: any) => onEditorStateChange(e, index)}
                            index={index}
                            isFocused={index === selectedIndex}
                          />
                        ) : (
                          <SmartAttachmentSpreadsheet
                            data={obj?.spreadsheetData}
                            baseClassName={obj?.className}
                            onChange={(e) => onSpreadsheetChange(e, index)}
                            onSpreadsheetChangeStyle={(e) => onSpreadsheetStyleChange(e, index)}
                            isFocused={index === selectedIndex}
                            hideIndicators={true}
                          />
                        )}
                      </div>
                    ))}
                  </div>
                </div>
                <>
                  {!isPrint && (
                    <ButtonMenu
                      icon={
                        <Menu.Button
                          type="button"
                          className="flex bg-blue-500 justify-center item-center rounded-full bg-blue-500 hover:bg-blue-600 border border-blue-600 hover:border-transparent text-blue-600 hover:text-white px-1 py-1 focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-opacity-75"
                        >
                          <PlusIcon className="w-4 h-4 text-white" />
                        </Menu.Button>
                      }
                      options={[
                        {
                          title: t('page.smart-attachment.add-opt-textarea'),
                          onClick: () => handleAddObject(ObjectType.TEXTAREA),
                        },
                        {
                          title: t('page.smart-attachment.add-opt-spreadsheet'),
                          onClick: () => handleAddObject(ObjectType.SPREADSHEET),
                        },
                      ]}
                    />
                  )}
                </>
              </InvoicePaperView>
            </div>
          </div>
        </Form>
      )}
    </Formik>
  );
};
